import React, { useEffect, useRef, useState } from 'react';
import { Check, Copy, Globe2, MapPin, Share2 } from 'lucide-react';

// Results surface in the reference's grammar ("Results for …" heading, meta
// line, action chips). Honest by design: the ranked list lives on the real
// Google SERP this panel links to — nothing cached or simulated in-page.
const SerpResultPanel = ({ result, onShare }) => {
  const headingRef = useRef(null);
  const [copied, setCopied] = useState(false);

  // Move focus to the fresh result (screen readers announce it; sighted users
  // land where the reference scrolls them).
  useEffect(() => {
    if (result) headingRef.current?.focus();
  }, [result]);

  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  if (!result) return null;

  const openSerp = () => {
    // Real user gesture: never popup-blocked. Open without 'noopener' to get a
    // handle, then sever the reverse reference (same trick as the form).
    const win = window.open(result.url, '_blank');
    if (win) win.opener = null;
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(result.url);
      setCopied(true);
    } catch (e) {
      /* clipboard unavailable (http/permissions) — button simply stays */
    }
  };

  const blocked = result.status === 'blocked';

  return (
    <section aria-label="SERP check result" className="mt-6 text-left">
      <h2
        ref={headingRef}
        tabIndex={-1}
        className="text-xl sm:text-2xl font-medium text-slate-800 outline-none"
      >
        Results for <span className="font-semibold">“{result.keyword}”</span>
      </h2>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
        <span className="inline-flex items-center gap-1.5">
          <Globe2 aria-hidden="true" className="w-4 h-4 text-slate-400" />
          {result.countryLabel}
          <span className="uppercase text-xs text-slate-400">({result.language})</span>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <MapPin aria-hidden="true" className="w-4 h-4 text-slate-400" />
          {result.city ? result.city : 'Country-wide'}
        </span>
        <span className="text-slate-400">Opened just now</span>
      </div>

      <div className="mt-3 rounded border border-[#dadee5] bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
          <p role="status" className={`text-sm leading-6 ${blocked ? 'text-amber-800' : 'text-slate-600'}`}>
            {blocked
              ? 'Your browser blocked the tab — open your live SERP below.'
              : 'Your live Google SERP opened in a new tab. Positions come straight from Google — nothing cached or simulated.'}
          </p>
          <div className="flex flex-wrap items-center gap-2 flex-none">
            <button type="button" onClick={openSerp} className="serp-chip serp-chip-primary">
              <Globe2 aria-hidden="true" className="w-4 h-4" />
              View Google Page
            </button>
            <button type="button" onClick={copyLink} className="serp-chip">
              {copied ? (
                <>
                  <Check aria-hidden="true" className="w-4 h-4 text-brand-600" />
                  Copied
                </>
              ) : (
                <>
                  <Copy aria-hidden="true" className="w-4 h-4" />
                  Copy link
                </>
              )}
            </button>
            {onShare && (
              <button type="button" onClick={onShare} className="serp-chip">
                <Share2 aria-hidden="true" className="w-4 h-4" />
                Share
              </button>
            )}
          </div>
        </div>
        {result.note && (
          <p role="status" className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            {result.note}
          </p>
        )}
      </div>
    </section>
  );
};

export default SerpResultPanel;
